import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useApi } from '../../useApi';
import '../../JoinSociety.css';
import './ClaimPlayerSheet.css';

// Dismissed for this session only: the next launch asks again, because a
// member with no player has no handicap and no scores until this is answered.
const dismissedFor = new Set();

/**
 * "IS THIS YOU?" for a member whose membership has no player linked.
 *
 * The invite page was the only join path that showed this step. Signing in
 * with a pending invite, the welcome card and the society modal all ignored
 * the server's `needs_claim` answer and left the membership unlinked — HCP
 * "— Set" doing nothing, no form, "No linked player" on Profile, and the
 * record the organiser created sitting orphaned. Home now asks
 * `/auth/claim-candidates` whenever the current membership is unlinked, which
 * covers every path and every member already stranded by them.
 */
const ClaimPlayerSheet = () => {
  const { currentSociety, userProfile, currentUser, fetchUserProfile, refreshPlayerStatus } = useAuth();
  const { authenticatedFetch } = useApi();
  const [candidates, setCandidates] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const membership = userProfile?.societies?.find((s) => s.id === currentSociety?.id);
  const unlinked = !!membership && !membership.player_id && !membership.player_profile;
  const societyId = currentSociety?.id;

  useEffect(() => {
    setCandidates(null);
    if (!unlinked || dismissedFor.has(societyId)) return undefined;
    let live = true;
    authenticatedFetch(`/auth/claim-candidates?society_id=${societyId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live && d && !d.linked) setCandidates(d.candidates || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [unlinked, societyId, authenticatedFetch]);

  if (!candidates) return null;

  const claim = async (playerId) => {
    setBusy(true); setError('');
    try {
      const res = await authenticatedFetch('/auth/claim-player', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(playerId
          ? { society_id: societyId, player_id: playerId }
          : { society_id: societyId, create_new: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not link your record. Please try again.'); return; }
      await fetchUserProfile(currentUser);
      if (refreshPlayerStatus) refreshPlayerStatus();
      setCandidates(null);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const later = () => { dismissedFor.add(societyId); setCandidates(null); };

  return (
    <div className="cps-backdrop" role="presentation">
      <div className="cps-sheet" role="dialog" aria-modal="true" aria-labelledby="cps-title">
        <h2 id="cps-title" className="cps-title">
          {candidates.length ? 'Is this you?' : 'Finish joining'}
        </h2>
        <p className="cps-text">
          {candidates.length
            ? <>We found {candidates.length === 1 ? 'a player record' : 'player records'} in <strong>{currentSociety?.name}</strong> that might be you. Linking keeps your handicap and past scores.</>
            : <>Your membership of <strong>{currentSociety?.name}</strong> has no player record yet, so there is no handicap or scores to show.</>}
        </p>
        {candidates.length > 0 && (
          <div className="claim-candidate-list">
            {candidates.map((c) => (
              <button key={c.player_id} type="button" className="claim-candidate-btn"
                      onClick={() => claim(c.player_id)} disabled={busy}>
                <span className="claim-candidate-name">{c.name}</span>
                <span className="claim-candidate-meta">
                  {c.handicap != null ? `HCP ${c.handicap}` : 'No handicap'}
                  {c.tier === 'possible' ? ' · possible match' : ''}
                </span>
              </button>
            ))}
          </div>
        )}
        {error && <p className="join-inline-error">{error}</p>}
        <button type="button" className="cps-primary" onClick={() => claim(null)} disabled={busy}>
          {busy ? 'Linking…' : candidates.length ? "None of these — I'm new here" : 'Create my player record'}
        </button>
        <button type="button" className="cps-later" onClick={later} disabled={busy}>Later</button>
      </div>
    </div>
  );
};

export default ClaimPlayerSheet;
